import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Divider, 
  CircularProgress,
  useTheme,
  Paper
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon, 
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  ListAlt as ListAltIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDisplay } from '../../components/PublicDisplayWrapper';
import { format, parseISO } from 'date-fns';
import config from '../../config';

function NotesDisplay({ isPublic = false, hardcodedToken = null }) {
  const { token } = useAuth();
  // Always call the hook, but only use its value if isPublic is true
  const publicDisplayContext = usePublicDisplay();
  // Use hardcoded token if provided, otherwise use the context token
  const publicToken = isPublic ? (hardcodedToken || publicDisplayContext.publicToken) : null;
  const theme = useTheme();
  
  // State for notes data
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Current time for the clock
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update the time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  // Format date for display
  const formatCreatedDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (error) {
      // If date parsing fails, try as a regular date
      try {
        return format(new Date(dateString), 'MMM d, yyyy');
      } catch (e) {
        console.error('Error formatting date:', e);
        return 'Unknown date';
      }
    }
  };

  // Fetch todos from API
  const fetchTodos = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use different endpoints based on whether this is public or authenticated
      let endpoint, headers;
      
      if (isPublic && publicToken) {
        endpoint = `${config.apiUrl}/public/todos?token=${publicToken}`;
        headers = { 'Content-Type': 'application/json' };
        console.log('Using public endpoint for todos:', endpoint);
      } else {
        endpoint = `${config.apiUrl}/todos`;
        headers = { 'Authorization': `Bearer ${token}` };
        console.log('Using authenticated endpoint for todos:', endpoint);
      }
      
      const response = await fetch(endpoint, { headers });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(`API error: ${response.status} ${errorData.error || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('Raw todos data from API:', data);
      
      // Filter out completed and archived todos and sort by order
      const activeTodos = data.filter(todo => !todo.completed && !todo.archived);
      const sortedTodos = [...activeTodos].sort((a, b) => {
        // Sort by order (same as in the Todo app)
        return (a.order || 0) - (b.order || 0);
      });
      
      setTodos(sortedTodos);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching todos:', error);
      setError(error.message || 'Failed to load notes');
      setTodos([]); // Set empty array to prevent processing errors
      setLoading(false);
    }
  }, [token, isPublic, publicToken]);

  useEffect(() => {
    // Fetch data
    fetchTodos();
    
    // Set up refresh interval (every 5 minutes)
    const refreshInterval = setInterval(() => {
      fetchTodos();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchTodos]);

  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: 2,
      height: '100vh', 
      overflow: 'hidden',
      bgcolor: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header with time and date - slimmed down to match TimeOffDisplay */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 2, 
          mb: 2,
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#d32f2f', // Red background
          color: 'white' // White text
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ListAltIcon sx={{ mr: 1, color: 'white' }} /> {/* White icon */}
          <Typography variant="h4">
            Note Board
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle1">
            {format(currentTime, 'EEEE, MMMM d, yyyy')}
          </Typography>
        </Box>
      </Paper>
      
      {/* Main content */}
      <Card 
        elevation={3}
        sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          borderTop: `6px solid ${theme.palette.secondary.main}`,
          overflow: 'hidden'
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListAltIcon sx={{ mr: 1, color: '#d32f2f', fontSize: 30 }} />
              <Typography variant="h5">Team Notes & Reminders</Typography>
            </Box>
          }
          sx={{ pb: 1 }}
        />
        <Divider />
        <CardContent sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          p: 0,
          '&:last-child': { 
            pb: 0 // Remove default padding bottom
          }
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={60} />
            </Box>
          ) : error ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="error" variant="h5">{error}</Typography>
            </Box>
          ) : todos.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" color="textSecondary">
                No notes found.
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}> {/* Remove default padding */}
              {todos.map((todo) => (
                <React.Fragment key={todo._id}>
                  <ListItem 
                    sx={{ 
                      py: 2,
                      px: 3,
                      bgcolor: todo.color || 'inherit',
                      transition: 'background-color 0.3s',
                      borderLeft: todo.completed 
                        ? '4px solid #4caf50'
                        : '4px solid #ff9800'
                    }}
                  >
                    <ListItemIcon>
                      {todo.completed ? (
                        <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
                      ) : (
                        <RadioButtonUncheckedIcon color="warning" sx={{ fontSize: 28 }} />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography 
                          variant="h6"
                          sx={{ 
                            textDecoration: todo.completed ? 'line-through' : 'none',
                            color: todo.completed ? 'text.secondary' : 'text.primary',
                            fontWeight: todo.completed ? 'normal' : 'medium'
                          }}
                        >
                          {todo.text}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {(todo.createdBy || todo.createdAt) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                              {todo.createdBy && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 0.5 }}>
                                  <PersonIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    Added by: {todo.createdBy.firstName} {todo.createdBy.lastName}
                                  </Typography>
                                </Box>
                              )}
                              {todo.createdAt && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                  <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    on {formatCreatedDate(todo.createdAt)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default NotesDisplay; 